'use client';

import React from 'react';
import Link from 'next/link';
import { Shield, Heart, Zap, Award, Globe } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

const values = [
  { icon: Heart, title: 'Paziente al Centro', desc: 'Ogni funzionalità è progettata per migliorare l\'esperienza del paziente e la qualità del servizio.' },
  { icon: Shield, title: 'Privacy & Sicurezza', desc: 'GDPR compliant by design. I dati sanitari sono trattati con il massimo livello di sicurezza.' },
  { icon: Zap, title: 'Innovazione Continua', desc: 'Aggiorniamo la piattaforma ogni settimana basandoci sul feedback dei nostri clienti.' },
  { icon: Globe, title: 'Made in Italy', desc: 'Sviluppato in Italia per i laboratori italiani, con supporto in italiano.' },
];

const team = [
  { name: 'Alessandro Ferrari', role: 'CEO & Co-founder', bio: 'Ex direttore IT di una rete di laboratori. Ha fondato LabCallManager dopo aver vissuto in prima persona i problemi di gestione delle chiamate.' },
  { name: 'Sofia Marino', role: 'CTO & Co-founder', bio: 'Ingegnera del software con 10 anni di esperienza in healthcare tech. Appassionata di sistemi distribuiti e real-time.' },
  { name: 'Marco Esposito', role: 'Head of Product', bio: 'Product manager con background in UX. Lavora a stretto contatto con i laboratori per capire le loro esigenze reali.' },
  { name: 'Chiara Bianchi', role: 'Head of Customer Success', bio: 'Garantisce che ogni laboratorio ottenga il massimo dalla piattaforma. Ex consulente sanitaria.' },
];

const certifications = [
  { name: 'GDPR Compliant', desc: 'Regolamento UE 2016/679 sulla protezione dei dati personali' },
  { name: 'ISO 27001', desc: 'Standard internazionale per la sicurezza delle informazioni' },
  { name: 'SOC 2 Type II', desc: 'Audit indipendente su sicurezza, disponibilità e riservatezza' },
  { name: 'HIPAA Ready', desc: 'Conformità agli standard USA per dati sanitari' },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-semibold text-sm text-foreground">LabCall<span className="text-primary">Manager</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground">Prezzi</Link>
            <Link href="/register/lab" className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90">Inizia Gratis</Link>
          </div>
        </div>
      </nav>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-foreground mb-4">La nostra missione</h1>
          <p className="text-xl text-muted-foreground leading-relaxed">
            Rendiamo la gestione delle comunicazioni nei laboratori di analisi semplice, efficiente e umana. 
            Perché ogni chiamata è un paziente che ha bisogno di aiuto.
          </p>
        </div>
      </section>
      {/* Story */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-4">La nostra storia</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                LabCallManager è nata nel 2022 dall'esperienza diretta di Alessandro Ferrari, che per anni ha gestito il reparto IT di una rete di laboratori di analisi in Lombardia.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Il problema era sempre lo stesso: le chiamate dei pazienti venivano gestite con strumenti generici non pensati per la sanità. Appuntamenti persi, follow-up dimenticati, operatori sopraffatti.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Oggi serviamo oltre 500 laboratori in tutta Italia, gestendo più di 2 milioni di chiamate al mese.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '2022', label: 'Anno di fondazione' },
                { value: '500+', label: 'Laboratori clienti' },
                { value: '2M+', label: 'Chiamate/mese' },
                { value: '15', label: 'Persone nel team' },
              ]?.map((s) => (
                <div key={s?.label} className="p-5 rounded-xl bg-muted/50 text-center">
                  <p className="text-2xl font-bold text-foreground">{s?.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{s?.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Values */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-foreground text-center mb-12">I nostri valori</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values?.map((v) => (
              <div key={v?.title} className="p-6 bg-white rounded-2xl border border-border">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <v.icon size={20} className="text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{v?.title}</h3>
                <p className="text-sm text-muted-foreground">{v?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Team */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-foreground text-center mb-12">Il nostro team</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {team?.map((m) => (
              <div key={m?.name} className="p-6 rounded-2xl border border-border text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-primary font-bold text-lg">{m?.name?.split(' ')?.map(n => n?.[0])?.join('')}</span>
                </div>
                <h3 className="font-semibold text-foreground mb-1">{m?.name}</h3>
                <p className="text-xs text-primary font-medium mb-3">{m?.role}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{m?.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Certifications */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-foreground text-center mb-4">Certificazioni & Conformità</h2>
          <p className="text-muted-foreground text-center mb-10">La sicurezza dei dati sanitari è la nostra priorità assoluta.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {certifications?.map((c) => (
              <div key={c?.name} className="p-5 bg-white rounded-xl border border-border text-center">
                <Award size={24} className="text-primary mx-auto mb-3" />
                <h4 className="font-semibold text-foreground text-sm mb-1">{c?.name}</h4>
                <p className="text-xs text-muted-foreground">{c?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Unisciti a 500+ laboratori</h2>
          <p className="text-primary-foreground/80 mb-6">Inizia oggi con 14 giorni di prova gratuita.</p>
          <Link href="/register/lab" className="px-8 py-3 bg-white text-primary rounded-xl font-semibold hover:bg-slate-50 transition-colors inline-block">
            Inizia Gratis
          </Link>
        </div>
      </section>
    </div>
  );
}
